import { useCallback, useEffect, useRef, type RefObject } from "react";

export type T_VisualViewportGeometry = {
  height: number;
  pageTop: number;
  scale: number;
};

export type T_MobileKeyboardViewportAdjustment = {
  dockTranslateY: number;
  shellTranslateY: number;
};

const ZERO_ADJUSTMENT: T_MobileKeyboardViewportAdjustment = {
  dockTranslateY: 0,
  shellTranslateY: 0,
};
const DEFAULT_VIEWPORT_SCALE = 1;
const VIEWPORT_SCALE_TOLERANCE = 0.01;

/**
 * Keeps the stable layout viewport visually anchored while moving only the
 * composer to the bottom of the shrunken visual viewport. The shell offset
 * compensates browser focus-panning; the dock offset compensates keyboard
 * occlusion. At non-default zoom we leave native browser behavior intact.
 */
export function getMobileKeyboardViewportAdjustment(
  baseline: T_VisualViewportGeometry,
  current: T_VisualViewportGeometry,
): T_MobileKeyboardViewportAdjustment {
  if (
    Math.abs(baseline.scale - DEFAULT_VIEWPORT_SCALE) >
      VIEWPORT_SCALE_TOLERANCE ||
    Math.abs(current.scale - DEFAULT_VIEWPORT_SCALE) > VIEWPORT_SCALE_TOLERANCE
  ) {
    return ZERO_ADJUSTMENT;
  }

  const occludedHeight = Math.max(0, baseline.height - current.height);
  return {
    dockTranslateY: occludedHeight === 0 ? 0 : -occludedHeight,
    shellTranslateY: current.pageTop - baseline.pageTop,
  };
}

function readVisualViewportGeometry(
  viewport: VisualViewport,
): T_VisualViewportGeometry {
  return {
    height: viewport.height,
    pageTop: viewport.pageTop,
    scale: viewport.scale,
  };
}

function resetKeyboardViewportStyles(shell: HTMLElement): void {
  shell.removeAttribute("data-mobile-keyboard-active");
  shell.style.removeProperty("--shell-mobile-keyboard-dock-translate-y");
  shell.style.removeProperty("--shell-mobile-keyboard-viewport-translate-y");
}

export function useMobileKeyboardViewport({
  active,
  anchorRef,
}: {
  active: boolean;
  anchorRef: RefObject<HTMLElement | null>;
}) {
  const baselineRef = useRef<T_VisualViewportGeometry | null>(null);

  const prepareBaseline = useCallback(() => {
    const viewport = window.visualViewport;
    baselineRef.current = viewport
      ? readVisualViewportGeometry(viewport)
      : null;
  }, []);
  const ensureBaseline = useCallback(() => {
    if (baselineRef.current === null) prepareBaseline();
  }, [prepareBaseline]);

  // eslint-disable-next-line no-restricted-syntax -- VisualViewport is an external browser subscription; imperative CSS variables avoid rerendering the shell during keyboard animation.
  useEffect(() => {
    const anchor = anchorRef.current;
    const shell = anchor?.closest<HTMLElement>(".shell-frame") ?? anchor;
    const viewport = window.visualViewport;
    if (!active || !shell || !viewport) return undefined;

    const baseline =
      baselineRef.current ?? readVisualViewportGeometry(viewport);
    baselineRef.current = baseline;
    document.documentElement.setAttribute(
      "data-mobile-keyboard-active",
      "true",
    );
    shell.setAttribute("data-mobile-keyboard-active", "true");

    let animationFrame: number | null = null;
    const update = () => {
      animationFrame = null;
      const adjustment = getMobileKeyboardViewportAdjustment(
        baseline,
        readVisualViewportGeometry(viewport),
      );
      shell.style.setProperty(
        "--shell-mobile-keyboard-dock-translate-y",
        `${adjustment.dockTranslateY}px`,
      );
      shell.style.setProperty(
        "--shell-mobile-keyboard-viewport-translate-y",
        `${adjustment.shellTranslateY}px`,
      );
    };
    const scheduleUpdate = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(update);
    };

    viewport.addEventListener("resize", scheduleUpdate);
    viewport.addEventListener("scroll", scheduleUpdate);
    window.addEventListener("resize", scheduleUpdate);
    update();

    return () => {
      viewport.removeEventListener("resize", scheduleUpdate);
      viewport.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }
      baselineRef.current = null;
      document.documentElement.removeAttribute("data-mobile-keyboard-active");
      resetKeyboardViewportStyles(shell);
    };
  }, [active, anchorRef]);

  return { ensureBaseline, prepareBaseline };
}
